<template>
  <b-modal
    :active.sync="isModalActive"
    :width="960"
    scroll="keep"
    class="weekly-edit-activity-modal"
    @close="onModalClose"
  >
    <div class="weekly-edit-activity-modal__card">
      <div class="weekly-edit-activity-modal__header">
        <div class="weekly-edit-activity-modal__title-row">
          <b-icon pack="fas" icon="pen" size="is-small" class="weekly-edit-activity-modal__title-icon" />
          <div>
            <h2 class="weekly-edit-activity-modal__title">
              {{ modalTitle }}
            </h2>
            <p class="weekly-edit-activity-modal__subtitle">
              {{ modalSubtitle }}
            </p>
          </div>
        </div>
      </div>

      <div v-if="isLoading" class="weekly-edit-activity-modal__loading">
        <b-loading :active="true" :is-full-page="false" />
      </div>

      <div v-else class="weekly-edit-activity-modal__body">
        <section v-if="showMeasurementsSection" class="weekly-edit-activity-modal__section">
          <div class="weekly-edit-activity-modal__section-head">
            <h3 class="weekly-edit-activity-modal__section-title">
              {{ $t('weekly_edit_activity_linked_measurements') }}
            </h3>
            <div class="weekly-edit-activity-modal__legend">
              <span class="weekly-edit-activity-modal__legend-item">
                <b-icon pack="fas" icon="calendar-alt" size="is-small" class="weekly-edit-activity-modal__legend-icon--primavera" />
                {{ $t('weekly_edit_activity_measurement_primavera') }}
              </span>
              <span class="weekly-edit-activity-modal__legend-item">
                <b-icon pack="fas" icon="ruler-combined" size="is-small" class="weekly-edit-activity-modal__legend-icon--engineering" />
                {{ $t('weekly_edit_activity_measurement_engineering') }}
              </span>
            </div>
          </div>
          
          <!-- tabla de mediciones vinculadas -->
          <div class="weekly-edit-activity-modal__table-wrap overflow-x-auto">
            <table class="weekly-edit-activity-modal__table w-full text-xs border-collapse min-w-max">
              <thead>
                <tr>
                  <th class="weekly-edit-activity-modal__th weekly-edit-activity-modal__th--slot" />
                  <th class="weekly-edit-activity-modal__th">{{ $t('code') }}</th>
                  <th class="weekly-edit-activity-modal__th weekly-edit-activity-modal__th--left">{{ $t('name') }}</th>
                  <th class="weekly-edit-activity-modal__th weekly-edit-activity-modal__th--qty">{{ $t('quantity') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="!measurementRows.length">
                  <td colspan="4" class="weekly-edit-activity-modal__td weekly-edit-activity-modal__td--empty">
                    {{ $t('no_results') }}
                  </td>
                </tr>
                <tr
                  v-for="(item, index) in measurementRows"
                  :key="item.id != null ? item.id : index"
                  class="weekly-edit-activity-modal__row"
                >
                  <td class="weekly-edit-activity-modal__td text-center">
                    <b-icon
                      pack="fas"
                      :icon="item.isImported ? 'calendar-alt' : 'ruler-combined'"
                      size="is-small"
                      :class="item.isImported
                        ? 'weekly-edit-activity-modal__slot-icon--primavera'
                        : 'weekly-edit-activity-modal__slot-icon--engineering'"
                    />
                  </td>
                  <td class="weekly-edit-activity-modal__td whitespace-nowrap">{{ item.code || '—' }}</td>
                  <td class="weekly-edit-activity-modal__td">{{ item.name || '—' }}</td>
                  <td class="weekly-edit-activity-modal__td">
                    <div class="weekly-edit-activity-modal__qty-field">
                      <b-numberinput
                        v-model="item.quantity"
                        :min="0"
                        controls-position="compact"
                        size="is-small"
                        icon-pack="fas"
                      />
                      <span v-if="item.unit" class="weekly-edit-activity-modal__qty-unit">{{ item.unit }}</span>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <p class="weekly-edit-activity-modal__hint">
            {{ $t('weekly_edit_activity_measurements_hint') }}
          </p>

          <button
            type="button"
            class="weekly-edit-activity-modal__add-measurement-btn"
            @click="openEngineeringMeasurementModal"
          >
            <b-icon pack="fas" icon="plus" size="is-small" />
            <span>{{ $t('weekly_add_engineering_measurement') }}</span>
          </button>
        </section>

        <section class="weekly-edit-activity-modal__section">
          <h3 class="weekly-edit-activity-modal__section-title">
            {{ $t('weekly_edit_activity_dates_people') }}
          </h3>
          <div class="weekly-edit-activity-modal__grid weekly-edit-activity-modal__grid--3">
            <b-field :label="$t('planned_start')">
              <b-datepicker
                v-model="form.currentStartDate"
                icon-pack="fas"
                :first-day-of-week="parseInt($t('first_day_of_week'))"
              />
            </b-field>
            <b-field :label="$t('planned_end')">
              <b-datepicker
                v-model="form.currentEndDate"
                icon-pack="fas"
                :min-date="form.currentStartDate"
                :first-day-of-week="parseInt($t('first_day_of_week'))"
              />
            </b-field>
            <b-field :label="$t('assigned_people_count')">
              <b-numberinput
                v-model="form.people"
                :min="0"
                controls-position="compact"
                size="is-small"
                icon-pack="fas"
              />
            </b-field>
          </div>
        </section>

        <section class="weekly-edit-activity-modal__section">
          <h3 class="weekly-edit-activity-modal__section-title">
            {{ $t('weekly_edit_activity_supervisor_manager') }}
          </h3>
          <div class="weekly-edit-activity-modal__grid weekly-edit-activity-modal__grid--2">
            <b-field :label="$t('supervisor')">
              <b-input
                value="—"
                readonly
              />
            </b-field>
            <b-field :label="$t('person_in_charge')">
              <b-autocomplete
                v-model="form.managerSearch"
                :data="managerOptions"
                field="fullname"
                open-on-focus
                clearable
                :placeholder="$t('weekly_edit_activity_not_assigned')"
                class="weekly-edit-activity-modal__manager-autocomplete"
                @typing="onManagerTyping"
                @select="onManagerSelected"
              >
                <template v-slot="props">
                  <div class="text-xs">
                    <span class="font-semibold">{{ props.option.fullname }}</span>
                    <span v-if="props.option.email" class="text-grey-dark"> ({{ props.option.email }})</span>
                  </div>
                </template>
                <template #empty>{{ $t('no_results') }}</template>
              </b-autocomplete>
            </b-field>
          </div>
        </section>

        <section class="weekly-edit-activity-modal__section weekly-edit-activity-modal__section--readonly">
          <div class="weekly-edit-activity-modal__section-head">
            <h3 class="weekly-edit-activity-modal__section-title">
              {{ $t('weekly_edit_activity_identification') }}
            </h3>
            <span class="weekly-edit-activity-modal__readonly-badge">{{ $t('weekly_edit_activity_readonly') }}</span>
          </div>
          <div class="weekly-edit-activity-modal__grid weekly-edit-activity-modal__grid--2">
            <b-field :label="identificationNameLabel">
              <b-input :value="readOnly.name" readonly />
            </b-field>
            <b-field :label="$t('planning_code')">
              <b-input :value="readOnly.code" readonly />
            </b-field>
            <b-field v-if="isSubactivity && readOnly.parentName" :label="$t('activity')">
              <b-input :value="readOnly.parentName" readonly />
            </b-field>
            <b-field :label="$t('kks')">
              <b-input :value="readOnly.kks" readonly />
            </b-field>
            <b-field :label="$t('activity_status')">
              <div class="weekly-edit-activity-modal__status-row">
                <div
                  class="weekly-edit-activity-modal__status-display"
                  :class="{ 'weekly-edit-activity-modal__status-display--pending': showPendingStatusPin }"
                >
                  <b-icon
                    v-if="showPendingStatusPin"
                    pack="fas"
                    icon="thumbtack"
                    size="is-small"
                    class="weekly-edit-activity-modal__status-pin"
                  />
                  <b-input :value="displayStatusLabel" readonly />
                </div>
                <b-checkbox
                  v-if="showPendingStatusControl"
                  v-model="form.removePendingReschedule"
                  class="weekly-edit-activity-modal__status-checkbox"
                >
                  {{ $t('weekly_remove_pending_reschedule') }}
                </b-checkbox>
              </div>
            </b-field>
          </div>
        </section>

        <section v-if="showMeasurementsSection" class="weekly-edit-activity-modal__section weekly-edit-activity-modal__section--readonly">
          <div class="weekly-edit-activity-modal__section-head">
            <h3 class="weekly-edit-activity-modal__section-title">
              {{ $t('budget') }}
            </h3>
            <span class="weekly-edit-activity-modal__readonly-badge">{{ $t('weekly_edit_activity_readonly') }}</span>
          </div>
          <div class="weekly-edit-activity-modal__grid weekly-edit-activity-modal__grid--2">
            <b-field :label="$t('weight')">
              <b-input :value="readOnly.weightLabel" readonly />
            </b-field>
            <b-field :label="$t('budget')">
              <b-input :value="readOnly.budgetLabel" readonly />
            </b-field>
          </div>
        </section>

        <section v-if="showMeasurementsSection" class="weekly-edit-activity-modal__section weekly-edit-activity-modal__section--readonly">
          <div class="weekly-edit-activity-modal__section-head">
            <h3 class="weekly-edit-activity-modal__section-title">
              {{ $t('weekly_edit_activity_progress_section') }}
            </h3>
            <span class="weekly-edit-activity-modal__readonly-badge">{{ $t('weekly_edit_activity_readonly') }}</span>
          </div>
          <div class="weekly-edit-activity-modal__grid weekly-edit-activity-modal__grid--3">
            <b-field :label="$t('baseline_progress')">
              <b-input :value="readOnly.baselineLabel" readonly />
            </b-field>
            <b-field :label="$t('baseline_latest')">
              <b-input :value="readOnly.latestPlanningLabel" readonly />
            </b-field>
            <b-field :label="$t('progress')">
              <b-input :value="readOnly.progressLabel" readonly />
            </b-field>
          </div>
        </section>
      </div>

      <div class="weekly-edit-activity-modal__footer">
        <button
          type="button"
          class="weekly-edit-activity-modal__cancel-btn"
          :disabled="isSaving"
          @click="close"
        >
          {{ $t('cancel') }}
        </button>
        <button
          type="button"
          class="weekly-edit-activity-modal__save-btn"
          :disabled="isSaving || isLoading"
          @click="submit"
        >
          {{ $t('weekly_edit_activity_save_changes') }}
        </button>
      </div>
    </div>

    <weekly-associate-engineering-measurement-modal
      ref="engineeringMeasurementModal"
      :project-id="projectId"
      @created="onEngineeringMeasurementCreated"
    />
  </b-modal>
</template>

<script>
import axios from 'axios'
import moment from 'moment'
import EventBus from '~/plugins/bus'
import WeeklyAssociateEngineeringMeasurementModal from './weekly-associate-engineering-measurement-modal.vue'

const ACTIVITY_STATUS_PENDING_RESCHEDULING = 9

const ACTIVITY_STATUS_LABEL_KEYS = [
  'activity_status_assigned',
  'activity_status_running',
  'activity_status_paused',
  'activity_status_issue',
  'activity_status_done',
  'activity_status_reopened',
  'activity_status_closed',
  'activity_status_delayed_start',
  'activity_status_delayed',
  'activity_status_pending_rescheduling',
]

function parseApiDateDMY(value) {
  if (!value) {
    return null
  }
  const parsed = moment(value, ['DD/MM/YYYY', 'YYYY-MM-DD'], true)
  return parsed.isValid() ? parsed.toDate() : null
}

function formatDateForApi(date) {
  if (!date) {
    return null
  }
  return moment(date).format('YYYY-MM-DD')
}

function formatPercent(value) {
  const num = parseFloat(value)
  if (Number.isNaN(num)) {
    return '—'
  }
  return `${num.toFixed(1)} %`
}

function buildManagerLabel(user) {
  if (!user) {
    return ''
  }
  if (user.fullname) {
    return user.fullname
  }
  return `${user.name || ''} ${user.surname || ''}`.trim()
}

function mapAssignmentRow(assignment) {
  const total = assignment.total != null && assignment.total !== ''
    ? parseFloat(assignment.total)
    : parseFloat(assignment.scheduled_quantity)
  return {
    id: assignment.id,
    code: assignment.code || '',
    name: assignment.name || assignment.description || '',
    unit: assignment.unit_name || assignment.unit || '',
    quantity: Number.isNaN(total) ? 0 : total,
    originalQuantity: Number.isNaN(total) ? 0 : total,
    isImported: assignment.boq_root_source === 'imported',
  }
}

export default {
  name: 'weekly-edit-activity-modal',

  components: {
    WeeklyAssociateEngineeringMeasurementModal,
  },

  props: {
    projectId: {
      type: Number,
      default: null,
    },
    rescheduleMode: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      isModalActive: false,
      isLoading: false,
      isSaving: false,
      activityId: null,
      activitySnapshot: null,
      measurementRows: [],
      managerOptions: [],
      form: {
        currentStartDate: null,
        currentEndDate: null,
        people: 0,
        managerId: null,
        managerSearch: '',
        removePendingReschedule: false,
      },
      readOnly: {
        name: '',
        code: '',
        kks: '—',
        statusLabel: '—',
        weightLabel: '—',
        budgetLabel: '—',
        baselineLabel: '—',
        latestPlanningLabel: '—',
        progressLabel: '—',
      },
      weeklyRowSnapshot: null,
      isSubactivity: false,
    }
  },

  computed: {
    showMeasurementsSection() {
      return !this.isSubactivity
    },

    modalTitle() {
      return this.isSubactivity
        ? this.$t('weekly_edit_subactivity_title')
        : this.$t('weekly_edit_activity_title')
    },

    modalSubtitle() {
      return this.isSubactivity
        ? this.$t('weekly_edit_subactivity_subtitle')
        : this.$t('weekly_edit_activity_subtitle')
    },

    identificationNameLabel() {
      return this.isSubactivity ? this.$t('subactivity') : this.$t('activity_name')
    },

    showPendingStatusControl() {
      if (!this.rescheduleMode || !this.activitySnapshot) {
        return false
      }
      const status = parseInt(this.activitySnapshot.status, 10)
      const previousStatus = this.activitySnapshot.previous_status
      return status === ACTIVITY_STATUS_PENDING_RESCHEDULING
        && previousStatus != null
        && previousStatus !== ''
        && !Number.isNaN(parseInt(previousStatus, 10))
    },

    showPendingStatusPin() {
      return this.showPendingStatusControl && !this.form.removePendingReschedule
    },

    displayStatusLabel() {
      if (this.showPendingStatusControl && this.form.removePendingReschedule) {
        return this.resolveStatusLabel(parseInt(this.activitySnapshot.previous_status, 10))
      }
      return this.readOnly.statusLabel
    },

    shouldApplyPendingStatusRemoval() {
      return this.showPendingStatusControl && this.form.removePendingReschedule
    },
  },

  methods: {
    show({ row = null, isSubactivity = null } = {}) {
      if (!row || row.id == null) {
        return
      }
      this.isSubactivity = isSubactivity != null
        ? isSubactivity
        : !!(row.is_subactivity || row.isSubactivity)
      this.activityId = row.id
      this.weeklyRowSnapshot = row
      this.isModalActive = true
      this.loadActivityData(row)
    },

    close() {
      this.isModalActive = false
    },

    onModalClose() {
      this.activityId = null
      this.activitySnapshot = null
      this.measurementRows = []
      this.managerOptions = []
      this.weeklyRowSnapshot = null
      this.isSubactivity = false
      this.form.removePendingReschedule = false
      this.isSaving = false
      this.isLoading = false
    },

    async loadActivityData(row) {
      if (!this.activityId) {
        return
      }

      this.isLoading = true
      try {
        const activityResponse = await axios.get(`/api/v2/activity/${this.activityId}`, {
          params: { with_statuses: 1 },
        })

        if (!activityResponse.data || !activityResponse.data.success) {
          this.$notify.error('error_loading_activity_data')
          this.close()
          return
        }

        const activity = activityResponse.data.data
        this.activitySnapshot = activity

        if (this.showMeasurementsSection) {
          const assignmentsResponse = await axios.get(`/api/v2/activity/${this.activityId}/assignments`, {
            params: { with_boq_root: 1 },
          })
          const assignments = assignmentsResponse.data && assignmentsResponse.data.success
            ? assignmentsResponse.data.data
            : []
          this.measurementRows = Array.isArray(assignments)
            ? assignments
              .filter((item) => !item.type || item.type === 'resource')
              .map((item) => mapAssignmentRow(item))
            : []
        } else {
          this.measurementRows = []
        }

        this.form.currentStartDate = parseApiDateDMY(activity.current_start_date)
        this.form.currentEndDate = parseApiDateDMY(activity.current_end_date)
        this.form.people = activity.people != null ? parseInt(activity.people, 10) : 0
        if (Number.isNaN(this.form.people)) {
          this.form.people = 0
        }

        const manager = activity.manager || null
        this.form.managerId = manager && manager.user_id != null ? manager.user_id : null
        this.form.managerSearch = buildManagerLabel(manager)
        this.form.removePendingReschedule = false

        this.readOnly = {
          name: activity.name || row.subactivityName || row.parentName || row.name || '—',
          code: activity.planning_code || row.code || row.parentIdLabel || '—',
          parentName: row.parentName || (activity.parent && activity.parent.name) || null,
          kks: '—',
          statusLabel: this.resolveStatusLabel(activity.status != null ? activity.status : row.status),
          weightLabel: activity.weight != null ? `${activity.weight} %` : (row.weight != null ? `${row.weight} %` : '—'),
          budgetLabel: '—',
          baselineLabel: formatPercent(row.baseline_line),
          latestPlanningLabel: formatPercent(row.latest_planning),
          progressLabel: formatPercent(row.progress != null ? row.progress : activity.progress),
        }
      } catch (error) {
        console.error('[weekly-edit-activity-modal] load error:', error)
        this.$notify.error('error_loading_activity_data')
        this.close()
      } finally {
        this.isLoading = false
      }
    },

    resolveStatusLabel(statusIndex) {
      if (statusIndex == null || statusIndex < 0 || statusIndex >= ACTIVITY_STATUS_LABEL_KEYS.length) {
        return '—'
      }
      return this.$t(ACTIVITY_STATUS_LABEL_KEYS[statusIndex])
    },

    onManagerTyping(value) {
      const query = value != null ? String(value).trim() : ''
      if (!query || query.length < 3 || !this.projectId) {
        this.managerOptions = []
        return
      }

      axios.get('/api/v2/activity/users/search', {
        params: {
          only_internal: 1,
          p: this.projectId,
          q: query,
        },
      }).then(({ data }) => {
        const users = data && data.success && Array.isArray(data.users)
          ? data.users
          : (data && Array.isArray(data.users) ? data.users : [])
        this.managerOptions = users.map((user) => ({
          ...user,
          id: user.id != null ? user.id : user.user_id,
          fullname: buildManagerLabel(user),
        }))
      }).catch(() => {
        this.managerOptions = []
      })
    },

    onManagerSelected(user) {
      if (!user) {
        this.form.managerId = null
        this.form.managerSearch = ''
        return
      }
      this.form.managerId = user.id != null ? user.id : user.user_id
      this.form.managerSearch = user.fullname || buildManagerLabel(user)
      this.managerOptions = []
    },

    openEngineeringMeasurementModal() {
      if (!this.$refs.engineeringMeasurementModal || !this.activitySnapshot) {
        return
      }
      this.$refs.engineeringMeasurementModal.show({
        activity: {
          id: this.activityId,
          name: this.activitySnapshot.name || this.readOnly.name,
        },
      })
    },

    onEngineeringMeasurementCreated() {
      if (this.weeklyRowSnapshot) {
        this.loadActivityData(this.weeklyRowSnapshot)
      }
      this.$emit('saved', { activity_id: this.activityId })
    },

    buildRescheduleStatusPayload() {
      return {
        id: this.activityId,
        current_start_date: formatDateForApi(this.form.currentStartDate),
        current_end_date: formatDateForApi(this.form.currentEndDate),
        people: this.resolvePeoplePayloadValue(),
        manager_user_id: this.form.managerId || null,
        status: parseInt(this.activitySnapshot.previous_status, 10),
      }
    },

    async applyPendingStatusRemoval() {
      if (!this.shouldApplyPendingStatusRemoval || !this.projectId) {
        return true
      }

      const { data } = await axios.post('/api/v2/activities/reschedule', {
        project_id: this.projectId,
        activities: [this.buildRescheduleStatusPayload()],
      })

      if (!data || !data.success) {
        this.$notify.error(
          (data && data.msg) ? data.msg : this.$t('error_rescheduling_activities')
        )
        return false
      }

      return true
    },

    buildActivityPayload() {
      const activity = this.activitySnapshot

      const payload = {
        activity_id: this.activityId,
        name: activity.name,
        description: activity.description || '',
        location_id: activity.location_id,
        moment_scheduled_start_date: formatDateForApi(parseApiDateDMY(activity.scheduled_start_date)),
        moment_scheduled_end_date: formatDateForApi(parseApiDateDMY(activity.scheduled_end_date)),
        moment_current_start_date: formatDateForApi(this.form.currentStartDate),
        moment_current_end_date: formatDateForApi(this.form.currentEndDate),
        moment_real_start_date: formatDateForApi(parseApiDateDMY(activity.real_start_date)),
        moment_real_end_date: formatDateForApi(parseApiDateDMY(activity.real_end_date)),
        moment_expected_end_date: formatDateForApi(parseApiDateDMY(activity.expected_end_date)),
        moment_gantt_expected_start_date: formatDateForApi(parseApiDateDMY(activity.gantt_expected_start_date)),
        moment_gantt_expected_end_date: formatDateForApi(parseApiDateDMY(activity.gantt_expected_end_date)),
        moment_gantt_real_start_date: formatDateForApi(parseApiDateDMY(activity.gantt_real_start_date)),
        moment_gantt_real_end_date: formatDateForApi(parseApiDateDMY(activity.gantt_real_end_date)),
        critical_path: activity.critical_path || null,
        weight: activity.weight,
        status_id: activity.status_id,
        activity_type_id: activity.activity_type_id,
        planning_code: activity.planning_code,
        show_on_devices: activity.show_on_devices,
        in_planning: activity.in_planning,
        people: this.resolvePeoplePayloadValue(),
        // No enviar updated_at: edit-activity.vue tampoco lo hace; si se envía,
        // la API aplica bloqueo optimista y puede devolver success: false sin error.
      }

      const originalManagerId = activity.manager && activity.manager.user_id != null
        ? activity.manager.user_id
        : null
      if (this.form.managerId && this.form.managerId !== originalManagerId) {
        payload.manager_user_added = this.form.managerId
      }
      if (originalManagerId && this.form.managerId !== originalManagerId) {
        payload.manager_user_deleted = originalManagerId
      }

      return payload
    },

    resolvePeoplePayloadValue() {
      const people = parseInt(this.form.people, 10)
      return Number.isNaN(people) || people < 0 ? 0 : people
    },

    validateSaveForm() {
      if (!this.form.currentStartDate || !this.form.currentEndDate) {
        this.$notify.error('please_complete_required_fields')
        return false
      }
      if (moment(this.form.currentEndDate).isBefore(this.form.currentStartDate, 'day')) {
        this.$notify.error('please_complete_required_fields')
        return false
      }
      if (!this.activitySnapshot || !this.activitySnapshot.location_id) {
        this.$notify.error('please_complete_required_fields')
        return false
      }
      return true
    },

    resolveSaveErrorMessage(error, responseData) {
      if (error && error.response && error.response.status === 422 && error.response.data.errors) {
        const errors = error.response.data.errors
        const firstKey = Object.keys(errors)[0]
        if (firstKey && errors[firstKey] && errors[firstKey][0]) {
          return errors[firstKey][0]
        }
      }
      if (responseData && responseData.msg) {
        return responseData.msg
      }
      return this.$t('error_creating_activity')
    },

    notifyFrontendRefresh() {
      EventBus.$emit('refreshActivitiesNotifications', true)
      EventBus.$emit('refreshAssignmentChangedNotifications', {
        activityId: this.activityId,
      })
    },

    buildChangedAssignmentsPayload() {
      return this.measurementRows
        .filter((item) => item.id != null && parseFloat(item.quantity) !== parseFloat(item.originalQuantity))
        .map((item) => ({
          id: item.id,
          total_units: parseFloat(item.quantity),
        }))
    },

    async submit() {
      if (this.isSaving || !this.activityId || !this.activitySnapshot) {
        return
      }
      if (!this.validateSaveForm()) {
        return
      }

      this.isSaving = true
      try {
        const activityPayload = this.buildActivityPayload()
        const { data: activityData } = await axios.post(`/api/v2/activity/${this.activityId}`, activityPayload)

        if (!activityData || !activityData.success) {
          this.$notify.error(this.resolveSaveErrorMessage(null, activityData))
          return
        }

        const changedAssignments = this.showMeasurementsSection
          ? this.buildChangedAssignmentsPayload()
          : []
        if (changedAssignments.length) {
          const { data: assignmentsData } = await axios.put(
            `/api/v2/assignments/edit/activity/${this.activityId}`,
            { assignment_resources: changedAssignments }
          )
          if (!assignmentsData || !assignmentsData.success) {
            this.$notify.error(
              (assignmentsData && assignmentsData.message)
                ? assignmentsData.message
                : this.$t('assignment_update_error')
            )
            return
          }
        }

        const statusRemovalApplied = await this.applyPendingStatusRemoval()
        if (!statusRemovalApplied) {
          return
        }

        this.$notify.success('success_editing_activity')
        this.notifyFrontendRefresh()
        this.$emit('saved', {
          activity_id: this.activityId,
          is_subactivity: this.isSubactivity,
        })
        this.close()
      } catch (error) {
        console.error('[weekly-edit-activity-modal] save error:', error)
        this.$notify.error(this.resolveSaveErrorMessage(error))
      } finally {
        this.isSaving = false
      }
    },
  },
}
</script>

<style scoped>
.weekly-edit-activity-modal__card {
  display: flex;
  flex-direction: column;
  max-height: min(90vh, 52rem);
  background-color: #fff;
  border-radius: 0.25rem;
  overflow: hidden;
}

.weekly-edit-activity-modal__header {
  flex-shrink: 0;
  padding: 0.875rem 1.25rem;
  background: linear-gradient(90deg, #2c426c 0%, #3b66bc 100%);
  color: #fff;
}

.weekly-edit-activity-modal__title-row {
  display: flex;
  align-items: flex-start;
  gap: 0.625rem;
}

.weekly-edit-activity-modal__title-icon {
  flex-shrink: 0;
  margin-top: 0.125rem;
}

.weekly-edit-activity-modal__title {
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.weekly-edit-activity-modal__subtitle {
  margin: 0.25rem 0 0;
  font-size: 0.75rem;
  opacity: 0.9;
}

.weekly-edit-activity-modal__loading {
  position: relative;
  flex: 1;
  min-height: 12rem;
  overflow-y: auto;
}

.weekly-edit-activity-modal__body {
  flex: 1;
  min-height: 0;
  padding: 1rem 1.25rem 0.5rem;
  overflow-x: hidden;
  overflow-y: auto;
}

.weekly-edit-activity-modal__section {
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e5e7eb;
}

.weekly-edit-activity-modal__section:last-child {
  border-bottom: none;
  margin-bottom: 0;
}

.weekly-edit-activity-modal__section--readonly {
  background-color: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  padding: 0.75rem;
}

.weekly-edit-activity-modal__section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}

.weekly-edit-activity-modal__section-title {
  margin: 0 0 0.75rem;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #374151;
}

.weekly-edit-activity-modal__section-head .weekly-edit-activity-modal__section-title {
  margin-bottom: 0;
}

.weekly-edit-activity-modal__legend {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  font-size: 0.625rem;
  font-weight: 600;
  color: #6b7280;
}

.weekly-edit-activity-modal__legend-item {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
}

.weekly-edit-activity-modal__legend-icon--primavera {
  color: #7c3aed;
}

.weekly-edit-activity-modal__legend-icon--engineering {
  color: #2563eb;
}

.weekly-edit-activity-modal__table-wrap {
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
}

.weekly-edit-activity-modal__table {
  background-color: #fff;
}

.weekly-edit-activity-modal__th {
  padding: 0.5rem 0.625rem;
  background-color: #f3f4f6;
  border-bottom: 1px solid #e5e7eb;
  font-size: 0.625rem;
  font-weight: 700;
  text-transform: uppercase;
  text-align: center;
  color: #4b5563;
}

.weekly-edit-activity-modal__th--left {
  text-align: left;
}

.weekly-edit-activity-modal__th--qty {
  min-width: 9rem;
}

.weekly-edit-activity-modal__th--slot {
  width: 2.5rem;
}

.weekly-edit-activity-modal__td {
  padding: 0.5rem 0.625rem;
  border-bottom: 1px solid #f3f4f6;
  vertical-align: middle;
}

.weekly-edit-activity-modal__td--empty {
  text-align: center;
  color: #6b7280;
  padding: 1.5rem;
}

.weekly-edit-activity-modal__slot-icon--primavera {
  color: #7c3aed;
}

.weekly-edit-activity-modal__slot-icon--engineering {
  color: #2563eb;
}

.weekly-edit-activity-modal__qty-field {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  justify-content: flex-end;
}

.weekly-edit-activity-modal__qty-unit {
  font-size: 0.6875rem;
  color: #6b7280;
  white-space: nowrap;
}

.weekly-edit-activity-modal__hint {
  margin: 0.5rem 0 0.75rem;
  font-size: 0.6875rem;
  color: #6b7280;
}

.weekly-edit-activity-modal__add-measurement-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.75rem;
  border: 1px solid #93c5fd;
  border-radius: 0.25rem;
  background-color: #eff6ff;
  color: #1d4ed8;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
}

.weekly-edit-activity-modal__add-measurement-btn:hover {
  background-color: #dbeafe;
}

.weekly-edit-activity-modal__grid {
  display: grid;
  gap: 0.75rem;
}

.weekly-edit-activity-modal__grid--2 {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.weekly-edit-activity-modal__grid--3 {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.weekly-edit-activity-modal__manager-autocomplete {
  width: 100%;
}

.weekly-edit-activity-modal__status-row {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 0.75rem;
}

.weekly-edit-activity-modal__status-display {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  flex: 1;
  min-width: 12rem;
}

.weekly-edit-activity-modal__status-display--pending .input {
  border-color: #fcd34d;
  background-color: #fffbeb;
}

.weekly-edit-activity-modal__status-pin {
  flex-shrink: 0;
  color: #92400e;
}

.weekly-edit-activity-modal__status-checkbox {
  margin-bottom: 0.375rem;
  white-space: nowrap;
}

.weekly-edit-activity-modal__readonly-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.125rem 0.5rem;
  border-radius: 9999px;
  background-color: #e5e7eb;
  color: #4b5563;
  font-size: 0.625rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.weekly-edit-activity-modal__footer {
  flex-shrink: 0;
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem 1.25rem;
  border-top: 1px solid #e5e7eb;
  background-color: #fff;
}

.weekly-edit-activity-modal__cancel-btn,
.weekly-edit-activity-modal__save-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 7rem;
  padding: 0.375rem 0.875rem;
  border-radius: 0.25rem;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
}

.weekly-edit-activity-modal__cancel-btn {
  border: 1px solid #d1d5db;
  background-color: #fff;
  color: #374151;
}

.weekly-edit-activity-modal__cancel-btn:hover:not(:disabled) {
  background-color: #f9fafb;
}

.weekly-edit-activity-modal__save-btn {
  border: 1px solid #3b66bc;
  background-color: #3b66bc;
  color: #fff;
}

.weekly-edit-activity-modal__save-btn:hover:not(:disabled) {
  background-color: #2c426c;
  border-color: #2c426c;
}

.weekly-edit-activity-modal__save-btn:disabled,
.weekly-edit-activity-modal__cancel-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
</style>

<style>
.weekly-edit-activity-modal .modal-content,
.weekly-edit-activity-modal .animation-content {
  max-height: calc(100vh - 2rem);
  overflow: visible;
}
</style>
